import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import ArtistLayout from '../components/layout/ArtistLayout';
import { 
  User, 
  Mail, 
  Phone, 
  Building2, 
  Calendar,
  Briefcase,
  MapPin,
  Camera,
  Save,
  UserCircle,
  Shield,
  Edit2,
  X,
  Check
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistProfile = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    phone: '',
    emergency_contact: '',
    bio: '',
    skills: []
  });
  const [skillInput, setSkillInput] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/artist/profile');
      setProfile(response.data);
      if (response.data.profile_extras) {
        setFormData({
          phone: response.data.profile_extras.phone || '',
          emergency_contact: response.data.profile_extras.emergency_contact || '',
          bio: response.data.profile_extras.bio || '',
          skills: response.data.profile_extras.skills || []
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/artist/profile', formData);
      toast.success('Profile updated successfully');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post('/artist/profile/upload-pic', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Profile picture updated');
      fetchProfile();
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, skillInput.trim()]
      });
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(s => s !== skill)
    });
  };

  if (loading) {
    return (
      <ArtistLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
        </div>
      </ArtistLayout>
    );
  }

  const designer = profile?.designer;
  const profileExtras = profile?.profile_extras || {};

  return (
    <ArtistLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="artist-profile">
        {/* Profile Header Card */}
        <Card className={cn("overflow-hidden", isDark ? "bg-slate-800 border-slate-700" : "")}>
          <div className="h-32 bg-gradient-to-r from-[#331062] to-[#5a1d8a]" />
          <div className="px-6 pb-6">
            <div className="flex flex-col md:flex-row items-start md:items-end gap-4 -mt-16">
              {/* Profile Picture */}
              <div className="relative">
                <div className={cn(
                  "w-32 h-32 rounded-full border-4 overflow-hidden",
                  isDark ? "border-slate-800 bg-slate-700" : "border-white bg-slate-100"
                )}>
                  {profileExtras.profile_pic_url ? (
                    <img 
                      src={`${BACKEND_URL}${profileExtras.profile_pic_url}`}
                      alt={user?.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[#CCFF00]">
                      <span className="text-4xl font-bold text-[#331062]">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className={cn(
                    "absolute bottom-0 right-0 p-2 rounded-full transition-colors",
                    "bg-[#331062] hover:bg-[#2A0D52] text-white",
                    uploading && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {uploading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="w-5 h-5" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* Name & Role */}
              <div className="flex-1 mt-4 md:mt-0">
                <h1 className={cn("text-2xl font-bold font-outfit", isDark ? "text-white" : "text-slate-800")}>
                  {user?.name}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                    {designer?.role || 'Artist'}
                  </span>
                  {designer?.location && (
                    <>
                      <span className={cn("text-xs", isDark ? "text-slate-600" : "text-slate-300")}>•</span>
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full",
                        designer.location === 'Noida' 
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                      )}>
                        📍 {designer.location}
                      </span>
                    </>
                  )}
                  {designer?.designer_type && (
                    <span className={cn(
                      "text-xs px-2 py-0.5 rounded-full",
                      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                    )}>
                      {designer.designer_type}
                    </span>
                  )}
                </div>
              </div>

              {/* Edit Button */}
              <Button
                onClick={() => setEditing(!editing)}
                variant={editing ? "destructive" : "outline"}
                className="flex items-center gap-2"
              >
                {editing ? (
                  <>
                    <X className="w-4 h-4" />
                    Cancel
                  </>
                ) : (
                  <>
                    <Edit2 className="w-4 h-4" />
                    Edit Profile
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Profile Details */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Basic Info */}
          <Card className={cn("p-6", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <h3 className={cn("text-lg font-semibold mb-4 flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
              <UserCircle className="w-5 h-5 text-[#331062]" />
              Basic Information
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                <div>
                  <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>Email</p>
                  <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>{user?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Briefcase className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                <div>
                  <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>Department</p>
                  <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                    {designer?.vertical_team || 'Not assigned'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building2 className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                <div>
                  <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>Manager</p>
                  <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                    {designer?.direct_manager || 'Not assigned'}
                  </p>
                </div>
              </div>
              {designer?.date_of_joining && (
                <div className="flex items-center gap-3">
                  <Calendar className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                  <div>
                    <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>Date of Joining</p>
                    <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                      {new Date(designer.date_of_joining).toLocaleDateString('en-IN', { 
                        day: 'numeric', month: 'long', year: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              )}
              {designer?.employee_id && (
                <div className="flex items-center gap-3">
                  <Shield className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                  <div>
                    <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>Employee ID</p>
                    <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                      {designer.employee_id}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Contact & Bio */}
          <Card className={cn("p-6", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <h3 className={cn("text-lg font-semibold mb-4 flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
              <User className="w-5 h-5 text-[#331062]" />
              Personal Details
            </h3>
            
            {editing ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter phone number"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="emergency">Emergency Contact</Label>
                  <Input
                    id="emergency"
                    value={formData.emergency_contact}
                    onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                    placeholder="Emergency contact number"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="bio">Bio</Label>
                  <textarea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Tell us about yourself..."
                    rows={3}
                    className={cn(
                      "w-full mt-1 px-3 py-2 rounded-md border text-sm resize-none",
                      isDark 
                        ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400" 
                        : "bg-white border-slate-300 text-slate-800"
                    )}
                  />
                </div>
                <div>
                  <Label>Skills</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      placeholder="Add a skill"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    />
                    <Button type="button" onClick={addSkill} size="sm">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.skills.map((skill, i) => (
                      <span 
                        key={i}
                        className={cn(
                          "px-2 py-1 rounded-full text-xs flex items-center gap-1",
                          isDark ? "bg-slate-700 text-slate-200" : "bg-slate-100 text-slate-700"
                        )}
                      >
                        {skill}
                        <button onClick={() => removeSkill(skill)} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="w-full bg-[#331062] hover:bg-[#2A0D52]"
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Phone className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                  <div>
                    <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>Phone</p>
                    <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                      {profileExtras.phone || 'Not provided'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                  <div>
                    <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>Emergency Contact</p>
                    <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                      {profileExtras.emergency_contact || 'Not provided'}
                    </p>
                  </div>
                </div>
                {profileExtras.bio && (
                  <div>
                    <p className={cn("text-xs mb-1", isDark ? "text-slate-500" : "text-slate-400")}>Bio</p>
                    <p className={cn("text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                      {profileExtras.bio}
                    </p>
                  </div>
                )}
                {profileExtras.skills?.length > 0 && (
                  <div>
                    <p className={cn("text-xs mb-2", isDark ? "text-slate-500" : "text-slate-400")}>Skills</p>
                    <div className="flex flex-wrap gap-2">
                      {profileExtras.skills.map((skill, i) => (
                        <span 
                          key={i}
                          className={cn(
                            "px-2 py-1 rounded-full text-xs",
                            isDark ? "bg-slate-700 text-slate-200" : "bg-slate-100 text-slate-700"
                          )}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
    </ArtistLayout>
  );
};

export default ArtistProfile;
